import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductModal from '../components/ProductModal';
import OrderDetailsModal from '../components/OrderDetailsModal';
import { 
  verifySession, 
  login as adminLogin,
  logout as adminLogout,
  adminProductsService, 
  adminOrdersService, 
  adminCustomersService, 
  adminSettingsService, 
  adminActivityLogsService 
} from '../services/adminApi';
import '../AdminStyles.css';

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('analytics');
  const navigate = useNavigate();

  // Verify admin session on component mount and periodically
  const verifyAdminSession = useCallback(async () => {
    try {
      // Verify the session is still valid using server-side API JWT verification
      const result = await verifySession();
      
      if (!result || !result.valid) {
        sessionStorage.removeItem('adminUsername');
        setIsAuthenticated(false);
      } else {
        sessionStorage.setItem('adminUsername', result.username);
        setIsAuthenticated(true);
      }
    } catch (err) {
      console.error('Session verification error:', err);
      setIsAuthenticated(false);
    } finally {
      setIsVerifying(false);
    }
  }, []);

  useEffect(() => {
    verifyAdminSession();
    
    // Re-verify session every 5 minutes
    const interval = setInterval(verifyAdminSession, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [verifyAdminSession]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await adminLogin(username, password);
      
      if (response && response.success) {
        sessionStorage.setItem('adminUsername', response.admin.username);
        setIsAuthenticated(true);
        // Log the successful login action securely
        await adminActivityLogsService.log('login', 'Admin logged in successfully', response.admin.username);
      } else {
        alert('Invalid username or password!');
      }
    } catch (err) {
      console.error('Login error:', err);
      alert(err.message || 'Login failed. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
      // Log the logout action securely
      const currentAdmin = sessionStorage.getItem('adminUsername') || 'admin';
      await adminActivityLogsService.log('logout', 'Admin logged out successfully', currentAdmin);
    } catch (err) {
      console.error('Failed to log logout action:', err);
    } finally {
      sessionStorage.removeItem('adminUsername');
      setIsAuthenticated(false);
      navigate('/');
    }
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Show loading while verifying session
  if (isVerifying) {
    return (
      <div className="admin-login">
        <div className="login-box">
          <h1>🔐 Verifying Session...</h1>
          <p>Please wait...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="login-box">
          <h1>🔐 Admin Login</h1>
          <form onSubmit={handleLogin}>
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            <button type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <button className={`sidebar-toggle ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(!sidebarOpen)}>
          <span></span>
          <span></span>
          <span></span>
        </button>
        <h1>⚙️ Admin Dashboard</h1>
        <button onClick={handleLogout} className="logout-btn">Logout</button>
      </div>

      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}

      <div className="dashboard-body">
        <div className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <button 
          className={activeTab === 'analytics' ? 'active' : ''}
          onClick={() => { setActiveTab('analytics'); setSidebarOpen(false); }}
        >
          📊 Analytics
        </button>
        <button 
          className={activeTab === 'products' ? 'active' : ''}
          onClick={() => { setActiveTab('products'); setSidebarOpen(false); }}
        >
          📦 Products
        </button>
        <button 
          className={activeTab === 'orders' ? 'active' : ''}
          onClick={() => { setActiveTab('orders'); setSidebarOpen(false); }}
        >
          🛒 Orders
        </button>
        <button 
          className={activeTab === 'customers' ? 'active' : ''}
          onClick={() => { setActiveTab('customers'); setSidebarOpen(false); }}
        >
          👥 Customers
        </button>
        <button 
          className={activeTab === 'payments' ? 'active' : ''}
          onClick={() => { setActiveTab('payments'); setSidebarOpen(false); }}
        >
          💳 Payments
        </button>
        <button 
          className={activeTab === 'discounts' ? 'active' : ''}
          onClick={() => { setActiveTab('discounts'); setSidebarOpen(false); }}
        >
          🎫 Discounts
        </button>
        <button 
          className={activeTab === 'settings' ? 'active' : ''}
          onClick={() => { setActiveTab('settings'); setSidebarOpen(false); }}
        >
          ⚙️ Settings
        </button>
        <button 
          className={activeTab === 'logs' ? 'active' : ''}
          onClick={() => { setActiveTab('logs'); setSidebarOpen(false); }}
        >
          📝 Logs
        </button>
        </div>

        <div className="admin-content">
        {activeTab === 'analytics' && <AnalyticsTab />}
        {activeTab === 'products' && <ProductsTab />}
        {activeTab === 'orders' && <OrdersTab />}
        {activeTab === 'customers' && <CustomersTab />}
        {activeTab === 'payments' && <PaymentsTab />}
        {activeTab === 'discounts' && <DiscountsTab />}
        {activeTab === 'settings' && <SettingsTab />}
        {activeTab === 'logs' && <ActivityLogsTab />}
        </div>
      </div>
    </div>
  );
};

const AnalyticsTab = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ordersData, productsData] = await Promise.all([
        adminOrdersService.getAll(),
        adminProductsService.getAll()
      ]);
      setOrders(ordersData || []);
      setProducts(productsData || []);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="analytics-tab"><p>Loading...</p></div>;

  const paidOrders = orders.filter(o => o.payment_status === 'paid' || o.payment_status === 'completed');
  const totalRevenue = paidOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
  const avgOrderValue = paidOrders.length > 0 ? totalRevenue / paidOrders.length : 0;

  // Calculate sales trend (last 7 days)
  const last7Days = Array.from({length: 7}, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    return date;
  });
  
  const salesData = last7Days.map(date => {
    const dateStr = date.toISOString().split('T')[0];
    const dayOrders = paidOrders.filter(o => o.created_at?.startsWith(dateStr));
    const daySales = dayOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      sales: daySales
    };
  });

  // Calculate best selling products
  const productSales = {};
  paidOrders.forEach(order => {
    if (order.items && Array.isArray(order.items)) {
      order.items.forEach(item => {
        const key = item.name || 'Unknown';
        productSales[key] = (productSales[key] || 0) + (item.quantity || 1);
      });
    }
  });
  
  const bestProducts = Object.entries(productSales)
    .map(([name, sales]) => ({ name, sales }))
    .sort((a, b) => b.sales - a.sales)
    .slice(0, 5);

  // Calculate quick stats
  const lowStockProducts = products.filter(p => p.stock < 10).length;
  const uniqueCustomers = new Set(orders.map(o => o.customer_email)).size;

  return (
    <div className="analytics-tab">
      <h2>📊 Store Analytics</h2>
      
      <div className="stats-grid">
        <div className="stat-card revenue">
          <h3>Total Revenue</h3>
          <p className="stat-number">${totalRevenue.toFixed(2)}</p>
        </div>
        <div className="stat-card orders">
          <h3>Total Orders</h3>
          <p className="stat-number">{paidOrders.length}</p>
        </div>
        <div className="stat-card products">
          <h3>Products</h3>
          <p className="stat-number">{products.length}</p>
        </div>
        <div className="stat-card avg">
          <h3>Avg Order Value</h3>
          <p className="stat-number">${avgOrderValue.toFixed(2)}</p>
        </div>
      </div>

      {salesData.some(d => d.sales > 0) && (
        <div className="charts-container">
          <div className="chart-box">
            <h3>📈 Sales Trend (Last 7 Days)</h3>
            <SalesChart data={salesData} />
          </div>

          {bestProducts.length > 0 && (
            <div className="chart-box">
              <h3>🏆 Best Selling Products</h3>
              <BestProductsChart data={bestProducts} />
            </div>
          )}
        </div>
      )}

      <div className="quick-stats">
        <div className="quick-stat">
          <span className="stat-label">Total Customers</span>
          <span className="stat-value">{uniqueCustomers}</span>
        </div>
        <div className="quick-stat">
          <span className="stat-label">Pending Orders</span>
          <span className="stat-value">{orders.filter(o => o.payment_status === 'pending').length}</span>
        </div>
        <div className="quick-stat">
          <span className="stat-label">Completed Orders</span>
          <span className="stat-value">{orders.filter(o => o.payment_status === 'completed').length}</span>
        </div>
        <div className="quick-stat">
          <span className="stat-label">Low Stock Items</span>
          <span className="stat-value">{lowStockProducts}</span>
        </div>
      </div>
    </div>
  );
};

const SalesChart = ({ data }) => {
  const maxSales = Math.max(...data.map(d => d.sales), 1);
  
  return (
    <div className="simple-chart">
      {data.map((item, idx) => (
        <div key={idx} className="chart-bar-container">
          <div 
            className="chart-bar"
            style={{ height: `${(item.sales / maxSales) * 100}%` }}
          >
            {item.sales > 0 && <span className="bar-value">${item.sales.toFixed(0)}</span>}
          </div>
          <span className="bar-label">{item.date}</span>
        </div>
      ))}
    </div>
  );
};

const BestProductsChart = ({ data }) => {
  const maxSales = Math.max(...data.map(d => d.sales), 1);
  
  return (
    <div className="horizontal-chart">
      {data.map((item, idx) => (
        <div key={idx} className="h-chart-row">
          <span className="h-chart-label">{item.name}</span>
          <div className="h-chart-bar-bg">
            <div 
              className="h-chart-bar"
              style={{ width: `${(item.sales / maxSales) * 100}%` }}
            >
              <span className="h-bar-value">{item.sales}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const data = await adminProductsService.getAll();
      setProducts(data || []);
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (id) => {
    try {
      const product = products.find(p => p.id === id);
      await adminProductsService.toggleActive(id, !product.active);
      await loadProducts();
      logActivity('Toggled product status', `Product ID: ${id}`);
    } catch (err) {
      console.error('Failed to toggle product:', err);
      alert('Failed to update product status');
    }
  };

  const deleteProduct = async (id) => {
    if (window.confirm('Delete this product?')) {
      try {
        await adminProductsService.delete(id);
        await loadProducts();
        logActivity('Deleted product', `Product ID: ${id}`);
      } catch (err) {
        console.error('Failed to delete product:', err);
        alert('Failed to delete product');
      }
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setIsModalOpen(true);
  };

  const handleEditProduct = (product) => {
    // Map Supabase product to modal format
    const mappedProduct = {
      id: product.id,
      name: product.name,
      description: product.description || '',
      imageUrl: product.image_url || '',
      stock: product.stock,
      category: product.category || 'cheat',
      active: product.active,
      price: product.price_pc || 0,
      features: product.features || [],
      displayOrder: product.display_order || 999
    };
    setEditingProduct(mappedProduct);
    setIsModalOpen(true);
  };

  const handleSaveProduct = async (productData) => {
    try {
      if (editingProduct) {
        // Update product via admin API
        await adminProductsService.update(editingProduct.id, {
          name: productData.name,
          price: parseFloat(productData.price) || 0,
          stock: parseInt(productData.stock) || 0,
          description: productData.description || '',
          category: productData.category || 'cheat',
          imageUrl: productData.imageUrl || '',
          features: Array.isArray(productData.features) ? productData.features : [],
          displayOrder: parseInt(productData.displayOrder) || 999
        });
        logActivity('Updated product', productData.name);
      } else {
        // Insert new product via admin API
        await adminProductsService.create({
          name: productData.name,
          price: parseFloat(productData.price) || 0,
          stock: parseInt(productData.stock) || 0,
          description: productData.description || '',
          category: productData.category || 'cheat',
          imageUrl: productData.imageUrl || '',
          features: Array.isArray(productData.features) ? productData.features : [],
          displayOrder: parseInt(productData.displayOrder) || 999
        });
        logActivity('Added product', productData.name);
      }
      await loadProducts();
      setIsModalOpen(false);
    } catch (err) {
      console.error('Failed to save product:', err);
      alert('Failed to save product: ' + err.message);
    }
  };

  const logActivity = async (action, details) => {
    try {
      await adminActivityLogsService.log(action, details);
    } catch (err) {
      console.error('Failed to log activity:', err);
    }
  };

  if (loading) return <div className="products-tab"><p>Loading...</p></div>;

  return (
    <div className="products-tab">
      <div className="tab-header">
        <h2>📦 Manage Products</h2>
        <button className="add-btn" onClick={handleAddProduct}>+ Add Product</button>
      </div>

      <ProductModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveProduct}
        product={editingProduct}
      />

      <table className="admin-table">
        <thead>
          <tr>
            <th>Order</th>
            <th>Name</th>
            <th>Price</th>
            <th>Stock</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {products.map(product => (
            <tr key={product.id}>
              <td>#{product.display_order || 999}</td>
              <td>{product.name}</td>
              <td>${product.price_pc || 0}</td>
              <td>
                <span className={`status-badge ${product.stock > 0 ? 'active' : 'inactive'}`}>
                  {product.stock > 0 ? 'AVAILABLE' : 'OUT OF STOCK'}
                </span>
              </td>
              <td>
                <span className={`status-badge ${product.active ? 'active' : 'inactive'}`}>
                  {product.active ? 'Active' : 'Hidden'}
                </span>
              </td>
              <td className="actions">
                <button onClick={() => handleEditProduct(product)} className="edit-btn">
                  Edit
                </button>
                <button onClick={() => toggleActive(product.id)} className="toggle-btn">
                  {product.active ? 'Hide' : 'Show'}
                </button>
                <button onClick={() => deleteProduct(product.id)} className="delete-btn">
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const data = await adminOrdersService.getAll();
      setOrders(data || []);
    } catch (err) {
      console.error('Failed to load orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await adminOrdersService.updateStatus(id, newStatus);
      await loadOrders();
    } catch (err) {
      console.error('Failed to update order:', err);
      alert('Failed to update order status');
    }
  };

  const sendConfirmationEmail = (order) => {
    // Show toast notification
    showToast('Email sender disabled for now. Contact @VillageBatman');
    
    // Shake animation
    const button = event.target;
    button.classList.add('shake');
    setTimeout(() => button.classList.remove('shake'), 500);
  };
  
  const showToast = (message) => {
    const toast = document.createElement('div');
    toast.className = 'toast-notification';
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => toast.classList.add('show'), 10);
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 2000);
  };

  const deleteOrder = async (id) => {
    if (window.confirm('Delete this order?')) {
      try {
        await adminOrdersService.delete(id);
        await loadOrders();
      } catch (err) {
        console.error('Failed to delete order:', err);
        alert('Failed to delete order');
      }
    }
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder({
      ...order,
      customer: order.customer_name,
      customerName: order.customer_name,
      customerEmail: order.customer_email,
      phone: order.customer_phone,
      region: order.customer_region,
      total: order.total_amount,
      status: order.payment_status,
      date: new Date(order.created_at).toLocaleDateString(),
      paymentMethod: 'Cryptocurrency',
      transactionId: order.payment_data?.txn_id || 'N/A'
    });
    setIsModalOpen(true);
  };

  if (loading) return <div className="orders-tab"><p>Loading...</p></div>;

  return (
    <div className="orders-tab">
      <h2>🛒 Manage Orders</h2>

      <OrderDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        order={selectedOrder}
      />

      <table className="admin-table">
        <thead>
          <tr>
            <th>Order ID</th>
            <th>Customer</th>
            <th>Items</th>
            <th>Total</th>
            <th>Status</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {orders.map(order => (
            <tr key={order.id}>
              <td>{order.order_number}</td>
              <td>{order.customer_name}</td>
              <td>
                {order.items && order.items.length > 0 ? (
                  <div style={{ fontSize: '0.9em' }}>
                    {order.items.map((item, idx) => (
                      <div key={idx}>
                        {item.name} ({item.platform}) x{item.quantity}
                      </div>
                    ))}
                  </div>
                ) : 'No items'}
              </td>
              <td>${order.total_amount}</td>
              <td>
                <select 
                  value={order.payment_status}
                  onChange={(e) => updateStatus(order.id, e.target.value)}
                  className="status-select"
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </td>
              <td>{new Date(order.created_at).toLocaleDateString()}</td>
              <td className="actions">
                <button onClick={() => viewOrderDetails(order)} className="view-btn">
                  View
                </button>
                <button onClick={() => sendConfirmationEmail(order)} className="edit-btn" title="Send confirmation email">
                  📧
                </button>
                <button onClick={() => deleteOrder(order.id)} className="delete-btn">
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CustomersTab = () => {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      // Get customers with stats from admin API
      const customersWithStats = await adminCustomersService.getAll();
      setCustomers(customersWithStats || []);
    } catch (err) {
      console.error('Failed to load customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Total Orders', 'Total Spent', 'Last Order'];
    const rows = filteredCustomers.map(c => [
      c.name,
      c.email,
      c.totalOrders,
      `$${c.totalSpent.toFixed(2)}`,
      c.lastOrder
    ]);

    let csv = headers.join(',') + '\n';
    rows.forEach(row => {
      csv += row.join(',') + '\n';
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) return <div className="customers-tab"><p>Loading...</p></div>;

  const filteredCustomers = customers.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="customers-tab">
      <div className="tab-header">
        <h2>👥 Customer Management</h2>
        <div className="header-actions">
          <input
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button className="export-btn" onClick={exportToCSV}>
            📄 Export CSV
          </button>
        </div>
      </div>

      <div className="stats-row">
        <div className="stat-box">
          <h4>Total Customers</h4>
          <p>{customers.length}</p>
        </div>
        <div className="stat-box">
          <h4>Total Revenue</h4>
          <p>${customers.reduce((sum, c) => sum + c.totalSpent, 0).toFixed(2)}</p>
        </div>
        <div className="stat-box">
          <h4>Avg Order Value</h4>
          <p>${(customers.reduce((sum, c) => sum + c.totalSpent, 0) / customers.reduce((sum, c) => sum + c.totalOrders, 0) || 0).toFixed(2)}</p>
        </div>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Total Orders</th>
            <th>Total Spent</th>
            <th>Last Order</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {filteredCustomers.map((customer, idx) => (
            <tr key={idx}>
              <td>{customer.name}</td>
              <td>{customer.email}</td>
              <td>{customer.totalOrders}</td>
              <td>${customer.totalSpent.toFixed(2)}</td>
              <td>{customer.lastOrder}</td>
              <td>
                <span className={`status-badge ${customer.totalOrders > 2 ? 'active' : 'inactive'}`}>
                  {customer.totalOrders > 2 ? 'VIP' : 'Regular'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {filteredCustomers.length === 0 && (
        <div className="no-data">
          <p>No customers found</p>
        </div>
      )}
    </div>
  );
};

const PaymentsTab = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      // Get all orders via admin API and filter for paid ones
      const allOrders = await adminOrdersService.getAll();
      const paidOrders = (allOrders || []).filter(o => 
        o.payment_status === 'paid' || o.payment_status === 'completed'
      );
      setPayments(paidOrders);
    } catch (err) {
      console.error('Failed to load payments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = (orderId) => {
    if (window.confirm('Process refund for this payment?')) {
      alert('Refund feature coming soon. Contact payment provider manually.');
    }
  };

  if (loading) return <div className="payments-tab"><p>Loading...</p></div>;

  const totalRevenue = payments.reduce((sum, p) => sum + (p.total_amount || 0), 0);

  return (
    <div className="payments-tab">
      <h2>💳 Payment Management</h2>
      
      <div className="stats-row">
        <div className="stat-box">
          <h4>Total Payments</h4>
          <p>{payments.length}</p>
        </div>
        <div className="stat-box">
          <h4>Total Revenue</h4>
          <p>${totalRevenue.toFixed(2)}</p>
        </div>
      </div>

      {payments.length === 0 ? (
        <div className="no-data">
          <p>No payments received yet</p>
        </div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Order Number</th>
              <th>Customer</th>
              <th>Amount</th>
              <th>Method</th>
              <th>Status</th>
              <th>Transaction ID</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.map(payment => (
              <tr key={payment.id}>
                <td>{payment.order_number}</td>
                <td>{payment.customer_email}</td>
                <td>${payment.total_amount}</td>
                <td>Cryptocurrency</td>
                <td>
                  <span className={`status-badge ${payment.payment_status === 'completed' ? 'active' : 'inactive'}`}>
                    {payment.payment_status}
                  </span>
                </td>
                <td>{payment.plisio_txn_id || payment.payment_data?.txn_id || 'N/A'}</td>
                <td>{new Date(payment.created_at).toLocaleDateString()}</td>
                <td className="actions">
                  <button onClick={() => handleRefund(payment.id)} className="delete-btn">Refund</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const DiscountsTab = () => {
  const [discounts, setDiscounts] = useState([
    { id: 1, code: 'SAVE10', discount: 10, type: 'percentage', expiry: '2024-12-31', active: true, usageLimit: 100, used: 23 },
    { id: 2, code: 'WELCOME5', discount: 5, type: 'fixed', expiry: '2024-06-30', active: true, usageLimit: 50, used: 12 },
  ]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ code: '', discount: '', type: 'percentage', expiry: '', usageLimit: '' });

  const handleAddDiscount = (e) => {
    e.preventDefault();
    const newDiscount = {
      id: Date.now(),
      ...formData,
      discount: parseFloat(formData.discount),
      usageLimit: parseInt(formData.usageLimit),
      active: true,
      used: 0
    };
    setDiscounts([...discounts, newDiscount]);
    setFormData({ code: '', discount: '', type: 'percentage', expiry: '', usageLimit: '' });
    setShowForm(false);
  };

  const toggleDiscount = (id) => {
    setDiscounts(discounts.map(d => d.id === id ? {...d, active: !d.active} : d));
  };

  const deleteDiscount = (id) => {
    if (window.confirm('Delete this discount code?')) {
      setDiscounts(discounts.filter(d => d.id !== id));
    }
  };

  return (
    <div className="discounts-tab">
      <div className="tab-header">
        <h2>🎫 Discount Codes</h2>
        <button className="add-btn" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Add Discount'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleAddDiscount} className="discount-form">
          <div className="form-row">
            <input type="text" placeholder="Code (e.g., SAVE10)" value={formData.code} onChange={(e) => setFormData({...formData, code: e.target.value})} required />
            <input type="number" placeholder="Discount" value={formData.discount} onChange={(e) => setFormData({...formData, discount: e.target.value})} required />
            <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})}>
              <option value="percentage">Percentage (%)</option>
              <option value="fixed">Fixed ($)</option>
            </select>
            <input type="date" value={formData.expiry} onChange={(e) => setFormData({...formData, expiry: e.target.value})} required />
            <input type="number" placeholder="Usage Limit" value={formData.usageLimit} onChange={(e) => setFormData({...formData, usageLimit: e.target.value})} required />
            <button type="submit" className="btn-save">Create</button>
          </div>
        </form>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Discount</th>
            <th>Type</th>
            <th>Expiry</th>
            <th>Usage</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {discounts.map(discount => (
            <tr key={discount.id}>
              <td><strong>{discount.code}</strong></td>
              <td>{discount.discount}{discount.type === 'percentage' ? '%' : '$'}</td>
              <td>{discount.type}</td>
              <td>{discount.expiry}</td>
              <td>{discount.used}/{discount.usageLimit}</td>
              <td><span className={`status-badge ${discount.active ? 'active' : 'inactive'}`}>{discount.active ? 'Active' : 'Inactive'}</span></td>
              <td className="actions">
                <button onClick={() => toggleDiscount(discount.id)} className="toggle-btn">{discount.active ? 'Disable' : 'Enable'}</button>
                <button onClick={() => deleteDiscount(discount.id)} className="delete-btn">Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SettingsTab = () => {
  const [settings, setSettings] = useState({
    storeName: 'IWantCheats Store',
    pageTitle: 'IWantCheats - Game Cheats Store',
    pageHeading: 'Premium Game Cheats',
    faviconUrl: '',
    logoUrl: '',
    contactEmail: 'iwantcheats.store@gmail.com',
    phone: '+1234567890',
    currency: 'USD',
    taxRate: 0,
    lowStockAlert: 10
  });
  const [loading, setLoading] = useState(true);
  const [settingsId, setSettingsId] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await adminSettingsService.get();
      
      if (data) {
        setSettingsId(data.id);
        setSettings({
          storeName: data.store_name || 'iwantcheats',
          pageTitle: data.page_title || 'iwantcheats',
          pageHeading: data.page_heading || 'Premium Game Cheats',
          faviconUrl: data.favicon_url || '',
          logoUrl: data.logo_url || '',
          contactEmail: data.contact_email || 'iwantcheats.store@gmail.com',
          phone: data.phone || '+1234567890',
          currency: data.currency || 'USD',
          taxRate: data.tax_rate || 0,
          lowStockAlert: data.low_stock_alert || 10
        });
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await adminSettingsService.update({
        id: settingsId,
        store_name: settings.storeName,
        page_title: settings.pageTitle,
        page_heading: settings.pageHeading,
        favicon_url: settings.faviconUrl,
        logo_url: settings.logoUrl,
        contact_email: settings.contactEmail,
        phone: settings.phone,
        currency: settings.currency,
        tax_rate: parseFloat(settings.taxRate),
        low_stock_alert: parseInt(settings.lowStockAlert),
        updated_at: new Date().toISOString()
      });
      
      alert('Settings saved successfully!');
    } catch (err) {
      console.error('Failed to save settings:', err);
      alert('Failed to save settings');
    }
  };

  if (loading) return <div className="settings-tab"><p>Loading...</p></div>;

  return (
    <div className="settings-tab">
      <h2>⚙️ Store Settings</h2>
      <form onSubmit={handleSave} className="settings-form">
        <div className="form-group">
          <label>Store Name</label>
          <input type="text" value={settings.storeName} onChange={(e) => setSettings({...settings, storeName: e.target.value})} />
        </div>
        <div className="form-group">
          <label>Page Title (Browser Tab)</label>
          <input type="text" value={settings.pageTitle} onChange={(e) => setSettings({...settings, pageTitle: e.target.value})} placeholder="IWantCheats - Game Cheats Store" />
        </div>
        <div className="form-group">
          <label>Favicon URL (Browser Tab Icon)</label>
          <input type="url" value={settings.faviconUrl} onChange={(e) => setSettings({...settings, faviconUrl: e.target.value})} placeholder="https://example.com/favicon.ico" />
        </div>
        <div className="form-group">
          <label>Page Heading (Homepage)</label>
          <input type="text" value={settings.pageHeading} onChange={(e) => setSettings({...settings, pageHeading: e.target.value})} placeholder="Premium Game Cheats" />
        </div>
        <div className="form-group">
          <label>Logo URL (Optional)</label>
          <input type="url" value={settings.logoUrl} onChange={(e) => setSettings({...settings, logoUrl: e.target.value})} placeholder="https://..." />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Contact Email</label>
            <input type="email" value={settings.contactEmail} onChange={(e) => setSettings({...settings, contactEmail: e.target.value})} />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input type="tel" value={settings.phone} onChange={(e) => setSettings({...settings, phone: e.target.value})} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Currency</label>
            <select value={settings.currency} onChange={(e) => setSettings({...settings, currency: e.target.value})}>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
            </select>
          </div>
          <div className="form-group">
            <label>Tax Rate (%)</label>
            <input type="number" step="0.01" value={settings.taxRate} onChange={(e) => setSettings({...settings, taxRate: e.target.value})} />
          </div>
          <div className="form-group">
            <label>Low Stock Alert</label>
            <input type="number" value={settings.lowStockAlert} onChange={(e) => setSettings({...settings, lowStockAlert: e.target.value})} />
          </div>
        </div>
        <button type="submit" className="btn-save">Save Settings</button>
      </form>
    </div>
  );
};

const ActivityLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      const data = await adminActivityLogsService.getAll();
      setLogs(data || []);
    } catch (err) {
      console.error('Failed to load logs:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="logs-tab"><p>Loading...</p></div>;

  const exportLogs = () => {
    const csv = 'Action,Details,Timestamp\n' + logs.map(log => 
      `${log.action},${log.details},${new Date(log.created_at).toLocaleString()}`
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="logs-tab">
      <div className="tab-header">
        <h2>📝 Activity Logs</h2>
        <button className="export-btn" onClick={exportLogs}>📄 Export Logs</button>
      </div>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Action</th>
            <th>Details</th>
            <th>Timestamp</th>
          </tr>
        </thead>
        <tbody>
          {logs.length > 0 ? logs.map(log => (
            <tr key={log.id}>
              <td><strong>{log.action}</strong></td>
              <td>{log.details}</td>
              <td>{new Date(log.created_at).toLocaleString()}</td>
            </tr>
          )) : (
            <tr><td colSpan="3">No activity logs yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AdminDashboard;
